import React, { useEffect, useState } from 'react';

import { PiCaretLeft, PiCaretRight } from 'react-icons/pi';
import styled from 'styled-components';

import { FormattedMessage } from '@techstyle/react-intl';

import { desktop, mobile } from '../../styles';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.black900};
  margin: 100px 0;

  ${mobile`
    margin: 20px 36px;
  `}
`;

const LabelContainer = styled.div`
  align-self: center;
  letter-spacing: 0.3px;
  font-size: 12px;
  font-weight: 375;
  line-height: 16px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  margin-top: 16px;
  gap: 16px;
  font-size: 14px;
  font-weight: 400;
  justify-content: center;
`;

const PageButton = styled.button<{ active?: boolean; isArrow?: boolean }>`
  line-height: 0;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 8px;
  cursor: pointer;
  border-radius: 48px;

  svg {
    stroke-width: 4px;
  }

  ${({ isArrow, theme }) => {
    if (isArrow) {
      return `background-color: ${theme.colors.lavender100};`;
    }

    return `border: 1px solid ${theme.colors.gray175};`;
  }};

  ${({ active, disabled, theme }) => {
    if (active) {
      return `
        background-color: ${theme.colors.black900};
        color: ${theme.colors.white};
      `;
    }
    if (disabled) {
      return `
        opacity: 0.5;
        cursor: not-allowed;

        svg {
          fill: ${theme.colors.lavender550};
        }
      `;
    }
  }}

  ${desktop`
    &:hover {
      border: 1px solid ${({ theme }) => theme.colors.black900};
    }
  `}
`;

const PaginationEllipsis = styled.div`
  display: flex;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 8px;
`;

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  startIndex: number;
  lastIndex: number;
  maxButtons: number;
  onPageChange: (page: number) => void;
};

const PaginationButtons = ({
  currentPage,
  totalPages,
  totalCount,
  maxButtons = 5,
  startIndex,
  lastIndex,
  onPageChange,
}: PaginationProps) => {
  const [pageNumbers, setPageNumbers] = useState([]);

  useEffect(() => {
    const pageNums: number[] = [];
    let startPage = 1;
    // if we have less than maxButtons, show that instead as last page
    let endPage = totalPages;
    if (totalPages > maxButtons) {
      if (currentPage <= 2) {
        // show 1, 2, 3
        startPage = 1;
        endPage = maxButtons;
      } else if (currentPage >= totalPages - 2) {
        // show the last 5 items
        startPage = totalPages - (maxButtons - 1);
        endPage = totalPages;
      } else {
        // always highlight the second button if there's ellipsis
        // and we're not on the first page
        startPage = currentPage - 1;
        endPage = currentPage + 3;
      }
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNums.push(i);
    }
    setPageNumbers(pageNums);
  }, [currentPage, maxButtons, totalPages]);

  const showEllipsis = (number, index) => {
    return totalPages > maxButtons && index === 3 && number !== totalPages - 1;
  };

  return (
    <Wrapper>
      <LabelContainer>
        <FormattedMessage
          id="site_product_grid.showing_count_items_of_total"
          defaultMessage="Showing {startIndex} - {lastIndex} items of {totalCount}"
          values={{
            startIndex,
            lastIndex,
            totalCount,
          }}
        />
      </LabelContainer>
      <ButtonsContainer>
        <PageButton
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          isArrow
        >
          <PiCaretLeft size={12} />
        </PageButton>
        {pageNumbers.map((number, index) => {
          if (showEllipsis(number, index)) {
            return <PaginationEllipsis key={number}>...</PaginationEllipsis>;
          }
          // always show the totalPages number on the last item
          if (index === pageNumbers.length - 1) {
            return (
              <PageButton
                key={totalPages}
                active={totalPages === currentPage}
                onClick={() => onPageChange(totalPages)}
              >
                {totalPages}
              </PageButton>
            );
          }
          return (
            <PageButton
              key={number}
              active={number === currentPage}
              onClick={() => onPageChange(number)}
            >
              {number}
            </PageButton>
          );
        })}
        <PageButton
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          isArrow
        >
          <PiCaretRight size={12} />
        </PageButton>
      </ButtonsContainer>
    </Wrapper>
  );
};

export default PaginationButtons;
